import { useEffect, useRef, useState } from "react";
import shared from "../../data/js/shared";

const {
	phoneVerified,
	welcomeLoggedIn,
	welcomeSignup,
	otpFailed,
	proceedLabel,
	resendPassword,
	resendSendingLabel,
	resendSentLabel,
	useDifferentLoginLabel,
} = shared.login;

/**
 * How long "Send a new code" stays off after a code goes out.
 *
 * The same minute the server holds between two sends to one address
 * (SEND_COOLDOWN_MS in utils/auth-challenge.ts). A second press inside it is
 * refused with CODE_COOLDOWN, so the button waits it out instead.
 */
export const RESEND_COOLDOWN_MS = 60 * 1000;

type ResendState = "idle" | "sending" | "sent";

export interface SuccessScreensProps {
	onProceed?: () => void;
	/** Send another code from the code step. Resolves true when one went out. */
	onResend?: () => Promise<boolean>;
}

/**
 * SuccessScreens Component
 *
 * Renders various success and error screens during the login flow
 *
 * @param onProceed - Optional callback when user clicks on success screen
 * @param onResend - Sends a new code without leaving the code step
 */
export const SuccessScreens = ({ onProceed, onResend }: SuccessScreensProps) => {
	const [resendState, setResendState] = useState<ResendState>("idle");
	const cooldownRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => () => {
		if (cooldownRef.current) clearTimeout(cooldownRef.current);
	}, []);

	/**
	 * "Send a new code" used to be a bare href="#" link. It cleared the hash, the
	 * view fell back to the first screen and no code was sent: the person had to
	 * find Continue and press it again. Now it sends, and the code step stays.
	 */
	const handleResend = async () => {
		if (!onResend || resendState !== "idle") return;
		setResendState("sending");
		let sent = false;
		try {
			sent = await onResend();
		} finally {
			// A refused send leaves the button ready: the error notice says why.
			if (!sent) setResendState("idle");
		}
		if (!sent) return;
		setResendState("sent");
		cooldownRef.current = setTimeout(() => setResendState("idle"), RESEND_COOLDOWN_MS);
	};

	const resendLabel =
		resendState === "sending" ? resendSendingLabel
		: resendState === "sent" ? resendSentLabel
		: resendPassword;

	return (
		<>
			<div className="phone-verified">
				<h2>{phoneVerified}</h2>
			</div>
			<div
				className="loggedin"
				onClick={onProceed}
				role="button"
				tabIndex={0}
				data-testid="loggedin-screen"
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onProceed?.();
					}
				}}
			>
				<div className="text">
					<h2>{welcomeLoggedIn}</h2>
					<p>{proceedLabel}</p>
				</div>
			</div>
			<div
				className="welcome"
				onClick={onProceed}
				role="button"
				tabIndex={0}
				data-testid="welcome-screen"
				onKeyDown={(e) => {
					if (e.key === "Enter" || e.key === " ") {
						e.preventDefault();
						onProceed?.();
					}
				}}
			>
				<div className="text">
					<h2>{welcomeSignup}</h2>
					<p>{proceedLabel}</p>
				</div>
			</div>
			<div className="otp-error">
				<h3>{otpFailed}</h3>
			</div>
			<div className="resendPassword">
				<button
					type="button"
					className="cursor-pointer font-semibold underline disabled:cursor-default disabled:no-underline"
					onClick={() => void handleResend()}
					disabled={resendState !== "idle"}
					aria-live="polite"
					data-testid="resend-code-button"
				>
					{resendLabel}
				</button>
				{/* The old link's job, kept: back to the first screen to change where the code goes. */}
				<a href="#" className="mt-2 block text-sm" data-testid="resend-back-link">
					{useDifferentLoginLabel}
				</a>
			</div>
		</>
	);
};
